"use client";

import { useState } from "react";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";

export default function FooterSection() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { createClient } = await import("@/lib/supabase/client");
      const supabase = createClient();
      const { error: dbError } = await supabase
        .from("newsletter_subscribers")
        .insert({ email: email.trim().toLowerCase() });
      if (dbError && dbError.code === "23505") {
        setError("You're already subscribed!");
        setLoading(false);
        return;
      }
      if (dbError) throw dbError;
      setSubscribed(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#0D0D0D] text-white overflow-hidden" aria-label="Footer and newsletter signup">
      {/* Top red bar */}
      <div className="h-1 w-full bg-[#FF2222]" />

      {/* Newsletter section */}
      <div className="max-w-8xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* Left */}
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF2222] block mb-5">
              Strike Force Newsletter
            </span>
            <h2 className="font-display text-5xl md:text-6xl font-black leading-[0.95] tracking-tightest mb-6">
              Join the<br />
              <span className="italic font-normal text-[#FF2222]">Strike Force.</span>
            </h2>
            <p className="text-base text-white/60 leading-relaxed font-light max-w-md">
              Get one email per week with our new primary advertiser target. Intel, scripts, and timing — delivered to your inbox. We strike together.
            </p>

            {/* Trust signals */}
            <div className="flex flex-wrap gap-6 mt-8 text-xs text-white/40 font-medium">
              {["214K subscribers", "No spam. Ever.", "Unsubscribe anytime"].map((item) => (
                <span key={item} className="flex items-center gap-1.5">
                  <Icon name="CheckCircleIcon" size={12} variant="solid" className="text-[#FF2222]" />
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Email form */}
          <div>
            {subscribed ? (
              <div className="bg-white/5 border border-white/10 rounded-4xl p-8 text-center animate-fade-up">
                <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircleIcon" size={28} variant="solid" className="text-green-400 animate-check-pop" />
                </div>
                <h3 className="font-display text-2xl font-black text-white mb-2">You&apos;re In.</h3>
                <p className="text-white/50 text-sm font-light">
                  Welcome to the Strike Force. Your first target brief arrives this Sunday.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubscribe}
                className="bg-white/5 border border-white/10 rounded-4xl p-8"
                noValidate
              >
                <label htmlFor="email-input" className="block text-sm font-semibold text-white/80 mb-3">
                  Your email address
                </label>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <input
                    id="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(""); }}
                    placeholder="you@example.com"
                    className="flex-1 bg-white/10 border border-white/20 rounded-2xl px-5 py-3.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF2222] focus:ring-1 focus:ring-[#FF2222] transition-colors"
                    aria-describedby={error ? "email-error" : undefined}
                    required
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center justify-center gap-2 bg-[#FF2222] text-white px-6 py-3.5 rounded-2xl text-sm font-semibold hover:bg-[#CC0000] transition-colors disabled:opacity-60 disabled:cursor-not-allowed btn-red-pulse whitespace-nowrap"
                    aria-label="Subscribe to the strike force newsletter"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Joining…
                      </>
                    ) : (
                      <>
                        <Icon name="BoltIcon" size={15} variant="solid" />
                        Subscribe
                      </>
                    )}
                  </button>
                </div>
                {error && (
                  <p id="email-error" className="text-xs text-[#FF2222] mt-1" role="alert">{error}</p>
                )}
                <p className="text-xs text-white/30 font-light">
                  No spam. Target briefing every Sunday. Unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5 px-6 md:px-12 py-8 max-w-8xl mx-auto">
        <div className="flex flex-col items-center gap-6">
          {/* Logo */}
          <AppLogo
            text="PaidMeansPaid"
            iconName="ShieldCheckIcon"
            size={28}
            className="opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
          />

          {/* Links */}
          <div className="flex items-center gap-6 text-xs font-medium text-white/40">
            <a href="#" className="hover:text-white/80 transition-colors">Privacy</a>
            <a href="#" className="hover:text-white/80 transition-colors">Terms</a>
            <a href="#" className="hover:text-white/80 transition-colors">Press Kit</a>
            <a href="/admin" className="hover:text-white/80 transition-colors">Admin</a>
          </div>

          {/* Socials */}
          <div className="flex items-center gap-4">
            <a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-white/40 hover:bg-white/10 transition-all"
              aria-label="Follow PaidMeansPaid on Twitter/X"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-white opacity-60" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-white/40 hover:bg-white/10 transition-all"
              aria-label="Follow PaidMeansPaid on Instagram"
            >
              <Icon name="CameraIcon" size={13} variant="outline" className="text-white opacity-60" />
            </a>
            <a
              href="https://tiktok.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:border-white/40 hover:bg-white/10 transition-all"
              aria-label="Follow PaidMeansPaid on TikTok"
            >
              <Icon name="PlayIcon" size={13} variant="solid" className="text-white opacity-60" />
            </a>
          </div>
        </div>

        <p className="text-center text-[11px] text-white/20 mt-8 font-light">
          © 2026 PaidMeansPaid. A consumer advocacy movement. Not affiliated with any streaming service.
        </p>
      </div>
    </footer>
  );
}